import axios from 'axios'
import { Decamelized } from 'humps'
import { DecamelizedTrailer, Trailer } from 'shared/models/trailer'
import { DecamelizedTruck, Truck } from 'shared/models/truck'

import { toCamelCase } from '../helpers/utils'
import { Address } from './address'
import { CreditApplicationStatus } from './cash'
import { Entitlement, ProductSubscription, ProductSubscriptionBundleName } from './productSubscription'
import { TruckListSetting } from './truckList'
import { DriverStatus, ListUser, StaffUser, User } from './user'

export enum TeamStatus {
  APPROVED = 'APPROVED',
  APPLYING = 'APPLYING',
  OFFBOARDED = 'OFFBOARDED',
  OFFBOARDING = 'OFFBOARDING',
  DEACTIVATED = 'DEACTIVATED',
  ONBOARDING = 'ONBOARDING',
}

export type TeamMember = {
  driverStatus?: DriverStatus
  dateJoined: string
  email: string
  firstName: string
  id: string
  isTeamAdmin: boolean
  isTeamDriver: boolean
  lastName: string
  mobile: string
  roles: string[]
}

export interface SlimTeamCarrier {
  id: string
  name: string
  mcNumber: string
  dotNumber: string
  billingAddress: string
  contactEmail: string
  phoneNumber: string
  legalNameOwner: string
  legalNameBusiness: string
}

export interface SlimTeam {
  id: string
  name?: string
  isApproved?: boolean
  isFlexStarter?: boolean
  isCreditOnly?: boolean
  hasStarterFeature?: boolean
  hasFactoringFeature?: boolean
  entitlements?: Entitlement[]
  creditStatus?: CreditApplicationStatus
  isTaxInfoComplete?: boolean
  productSubscriptionName?: ProductSubscriptionBundleName
  numActiveDrivers: number
  carrier?: SlimTeamCarrier
  trucks?: Truck[]
  trailers?: Trailer[]
}

export interface Team extends SlimTeam {
  numPowerUnits?: number
  numTrailers?: number
  composition?: string
  dotNumber?: string
  mcNumber?: string
  locationCity?: string
  locationState?: string
  referralSource?: string
  dateCreated?: string
  dateUpdated?: string
  dateEsignActConsent?: string
  numTrailersDryVan?: number
  numTrailersFlatbed?: number
  numTrailersReefer?: number
  stripeAccountCreated?: boolean
  billingAddress: Address
  ratesVisibleToNonAdmins?: boolean
  productSubscription: ProductSubscription
  ctMcNumber: string
  plaidAssetsEligible?: boolean
  businessType?: BusinessType
  businessDocumentation?: string
  legalNameBusiness?: string
  legalFormationState?: string
  legalBusinessOwner?: string
  isCreditStatusOpenToUser?: boolean
  hasTaxId?: boolean
  formattedSubscriptionName?: boolean
  truckListSetting: TruckListSetting
  status: TeamStatus
  greyList: TeamGreyList
  members: Array<ListUser>
}

export type DecamelizedTeam = Decamelized<Omit<Team, 'billingAddress'>> & {
  billing_address: Address
}

export type ConsoleTeam = Decamelized<
  Omit<Team, 'trucks' | 'trailers'> & {
    status?: string
    offboardingStatus?: string | null
    members: TeamMember[]
    weeklyRevenueDollars: string | null
  }
> & {
  trucks: DecamelizedTruck[]
  trailers: DecamelizedTrailer[]
}

export interface TeamGreyList {
  onGreyList: boolean
  reasonCodes: TeamGreyListReasonCode[]
}

export enum TeamGreyListReasonCode {
  EXCESSIVE_ADVANCES = 'EXCESSIVE_ADVANCES',
  CREDIT_FRAUD = 'CREDIT_FRAUD',
  CREDIT_OVERDUE_BALANCE = 'CREDIT_OVERDUE_BALANCE',
}

export enum BusinessType {
  LLC = 'LLC',
  CORP = 'CORP',
  SOLE_PROPRIETORSHIP = 'SP',
}

// TODO: extend TeamV3 with more fields
export interface TeamV3 extends Team {
  members: User[]
}

export function getTeamV3(id: string | undefined) {
  const url = `/api/v3/teams/${id}/`

  return axios.get(url).then((response) => ({
    team: response.data ? (toCamelCase(response.data) as TeamV3) : undefined,
  }))
}

export function getStaffUsers(): Promise<StaffUser[]> {
  const url = `/api/v3/teams/staff-users/`

  return axios.get(url).then((response) => {
    return response.data.map(toCamelCase) as StaffUser[]
  })
}

export interface FleetConfirmDetailsPayload {
  formData: FormData
  teamId: string
  userId: string
}

export function confirmTeamV3Details({ teamId, formData, userId }: FleetConfirmDetailsPayload) {
  const url = `/api/v3/teams/${teamId}/confirm-details/?user_id=${userId}`
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, formData, config)
}

export function updateTeamV3Info(teamId: string, payload: Partial<DecamelizedTeam>) {
  let url = `/api/v3/teams/${teamId}/`
  return axios.patch(url, payload).then((response) => {
    return response.data
  })
}

export enum BusinessDocumentationParserStatus {
  UNVERIFIED = 'UNVERIFIED',
  VERIFIED = 'VERIFIED',
  FAILED = 'FAILED',
}

export interface BusinessDocumentationParserData {
  id: string
  status: BusinessDocumentationParserStatus
  isVerified: boolean
  formattedErrors: string[]
  documentData: {
    isCertificateValid: boolean
    isW9: boolean
    isIrs: boolean
    isEntityNameValid?: boolean
    isManagingMembersValid?: boolean
    isSecretaryStateValid?: boolean
    entityName?: string
    secretaryState?: string
    managingMembers?: string[]
  }
}

export function businessDocumentationParser(
  teamId: string,
  userId?: string
): Promise<BusinessDocumentationParserData> {
  let url = `/api/v3/teams/${teamId}/business-documentation-parser/${userId ? `?user_id=${userId}` : ''}`
  return axios.post(url).then((response) => {
    return toCamelCase(response.data) as BusinessDocumentationParserData
  })
}

export const optIntoOnboardingInsurance = (teamId: string, formData: FormData) => {
  let url = `/api/v3/teams/${teamId}/onboarding-insurance-opt-in/`
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  return axios.post(url, formData, config)
}

export const enrollIntoOnboardingInsurance = (teamId: string, payload: any) => {
  const url = `/api/v1/console/teams/${teamId}/onboarding-insurance-enrollment/`
  return axios.post(url, payload)
}
